import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, Phone, ArrowRight } from "lucide-react";
import { ZOOMY } from "@/constants/zoomyIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTEREST_OPTIONS = [
  { value: "rent", label: "Rent a Zoomy" },
  { value: "buy", label: "Buy / Dealership" },
  { value: "partner", label: "Financing / Partner" },
];

export const ContactForm = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    interest: "rent",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || form.name.trim().length < 2) {
      setError("Please enter your full name.");
      return;
    }
    if (!form.phone.trim() || form.phone.trim().length < 6) {
      setError("Please enter a valid phone number.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        interest: form.interest,
        message: form.message.trim() || null,
      };
      await axios.post(`${API}/leads`, payload);
      setSuccess(true);
      setForm({ name: "", phone: "", email: "", interest: "rent", message: "" });
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={ZOOMY.contactSection}
      className="relative py-24 sm:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left: copy */}
          <div className="lg:col-span-5">
            <div className="font-mono text-xs uppercase tracking-[0.28em] text-lime-400 mb-5">
              // 05 — Talk to us
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.95]">
              One form. <br />
              Three ways to <span className="text-lime-400">ride with us</span>.
            </h2>
            <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed">
              Want to rent a Zoomy, buy from our dealership, or partner with us as a
              financier? Drop your details — a human responds within 24 hours.
            </p>

            <div className="mt-10 border-t border-white/10 pt-8 space-y-5">
              <a
                href="tel:9217470162"
                data-testid={ZOOMY.footerPhone1}
                className="group flex items-center justify-between border border-white/10 px-5 py-4 hover:border-lime-400/70 transition-colors"
              >
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                    Direct line
                  </div>
                  <div className="font-display text-xl font-bold text-white mt-1">+91 92174 70162</div>
                </div>
                <Phone className="w-5 h-5 text-lime-400 group-hover:scale-110 transition-transform" />
              </a>
              <a
                href="tel:9217476487"
                data-testid={ZOOMY.footerPhone2}
                className="group flex items-center justify-between border border-white/10 px-5 py-4 hover:border-lime-400/70 transition-colors"
              >
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                    Direct line
                  </div>
                  <div className="font-display text-xl font-bold text-white mt-1">+91 92174 76487</div>
                </div>
                <Phone className="w-5 h-5 text-lime-400 group-hover:scale-110 transition-transform" />
              </a>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <div className="relative border border-white/10 bg-[#080808] p-7 sm:p-10">
              {success ? (
                <div
                  data-testid={ZOOMY.leadSuccess}
                  className="py-16 flex flex-col items-center text-center"
                >
                  <CheckCircle2 className="w-14 h-14 text-lime-400" strokeWidth={1.5} />
                  <h3 className="font-display text-3xl font-bold mt-6 text-white">
                    We&apos;ve got it.
                  </h3>
                  <p className="text-slate-400 mt-3 max-w-md">
                    Thanks for reaching out to Zoomy. A team member will be in touch within
                    24 hours. For anything urgent, just call us directly.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSuccess(false)}
                    data-testid="lead-reset-btn"
                    className="mt-8 inline-flex items-center gap-2 text-lime-400 font-bold uppercase tracking-wider text-sm hover:gap-3 transition-all"
                  >
                    Send another <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={onSubmit}
                  data-testid={ZOOMY.leadForm}
                  className="space-y-7"
                  noValidate
                >
                  <div className="grid sm:grid-cols-2 gap-7">
                    <Field label="Your name" required>
                      <input
                        type="text"
                        value={form.name}
                        onChange={onChange("name")}
                        data-testid={ZOOMY.leadName}
                        placeholder="e.g. Ravi Sharma"
                        className="w-full bg-transparent border-b border-white/15 text-white placeholder-slate-600 focus:border-lime-400 outline-none py-3 text-base"
                      />
                    </Field>
                    <Field label="Phone" required>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={onChange("phone")}
                        data-testid={ZOOMY.leadPhone}
                        placeholder="10-digit mobile"
                        className="w-full bg-transparent border-b border-white/15 text-white placeholder-slate-600 focus:border-lime-400 outline-none py-3 text-base"
                      />
                    </Field>
                  </div>

                  <Field label="Email (optional)">
                    <input
                      type="email"
                      value={form.email}
                      onChange={onChange("email")}
                      data-testid={ZOOMY.leadEmail}
                      placeholder="you@example.com"
                      className="w-full bg-transparent border-b border-white/15 text-white placeholder-slate-600 focus:border-lime-400 outline-none py-3 text-base"
                    />
                  </Field>

                  <Field label="I'm interested in" required>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2" data-testid={ZOOMY.leadInterest}>
                      {INTEREST_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          data-testid={`interest-${opt.value}`}
                          onClick={() => setForm((f) => ({ ...f, interest: opt.value }))}
                          className={`px-3 py-3 text-xs sm:text-sm uppercase tracking-wider font-bold border transition-all text-center ${
                            form.interest === opt.value
                              ? "bg-lime-400 text-black border-lime-400"
                              : "bg-transparent text-slate-300 border-white/15 hover:border-lime-400/60"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </Field>

                  <Field label="Message (optional)">
                    <textarea
                      rows={3}
                      value={form.message}
                      onChange={onChange("message")}
                      data-testid={ZOOMY.leadMessage}
                      placeholder="Tell us what you need…"
                      className="w-full bg-transparent border-b border-white/15 text-white placeholder-slate-600 focus:border-lime-400 outline-none py-3 text-base resize-none"
                    />
                  </Field>

                  {error && (
                    <div
                      data-testid="lead-error"
                      className="text-sm text-red-400 border-l-2 border-red-400 pl-3"
                    >
                      {error}
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 pt-2">
                    <p className="text-[11px] text-slate-500 max-w-sm leading-relaxed">
                      By submitting, you agree to be contacted by the Zoomy team.
                    </p>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid={ZOOMY.leadSubmit}
                      className="btn-lime w-full sm:w-auto justify-center inline-flex items-center gap-3 bg-lime-400 text-black font-bold uppercase tracking-wider text-sm px-7 py-4 hover:bg-lime-300 disabled:opacity-60 transition-colors"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" /> Sending…
                        </>
                      ) : (
                        <>
                          Send enquiry <ArrowRight className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
      {label}
      {required && <span className="text-lime-400 ml-1">*</span>}
    </span>
    {children}
  </label>
);
